import type {
  BillToDTO,
  EmergencyContact,
  QuoteStopType,
  ShipmentShare,
} from '@fv/client-types'

import type {
  ApiAwardLoadEntryDTO,
  ApiAwardLoadsPayload,
} from '@/types/api-dto'
import { removeNullProperties } from '@/utils/removeNullProperties'

import { billToMapper } from '../billTo/billToFuncs'
import { buildApiHandlingUnitFromFormCommodity } from '../commodities/buildApiHandlingUnit'
import type { FormCommodity } from '../commodities/FormCommodity'
import { buildHandlingUnitDTO } from '../commodities/load-items/loadItemHelpers'
import {
  buildLoadCustomsBrokerDTO,
  type CustomsBroker,
} from '../customsBroker/brokerTypes'
import type { ShipmentLocationFormModel } from '../shipment-location-form/types'
import { toApiLocation } from '../shipment-location-form/types'
import type { ShipmentFormModel } from './types'

export function buildAwardLoadsPayloadCommon(args: {
  createBOL: boolean
  loads: ApiAwardLoadEntryDTO[]
  locations: ShipmentLocationFormModel[]
  shares?: ShipmentShare[]
  brandId?: string
  billTo?: BillToDTO
  customsBroker?: CustomsBroker | CustomsBroker[]
  emergencyContact?: EmergencyContact
  glCode?: string | null
  rateLocationType?: QuoteStopType
}): ApiAwardLoadsPayload {
  return {
    createBOL: args.createBOL,
    loads: args.loads,
    locations: args.locations.map(toApiLocation),
    ...(args.shares && { shares: args.shares }),
    ...(args.glCode !== undefined &&
      args.glCode !== null && { glCode: args.glCode }),
    ...(args.brandId && { brandId: args.brandId }),
    ...(args.billTo && { billTo: billToMapper.forApiPayload(args.billTo) }),
    ...(args.customsBroker && {
      customsBroker: Array.isArray(args.customsBroker)
        ? args.customsBroker
        : [args.customsBroker],
    }),
    ...(args.emergencyContact && { emergencyContact: args.emergencyContact }),
    ...(args.rateLocationType && { rateLocationType: args.rateLocationType }),
  }
}

export function buildTruckloadAwardLoadEntry(
  load: {
    loadId: string
    quoteId: string
    equipment: {
      accessorials?: ApiAwardLoadEntryDTO['equipment']['accessorials']
    }
    items: FormCommodity[]
  },
  isPickupOnly: boolean,
): ApiAwardLoadEntryDTO {
  return {
    equipment: {
      accessorials:
        load.equipment.accessorials?.map(removeNullProperties) ?? [],
    },
    loadId: load.loadId,
    quoteId: load.quoteId,
    ...(!isPickupOnly && {
      items: load.items.map(item =>
        buildApiHandlingUnitFromFormCommodity(item, 'truckload'),
      ),
    }),
  }
}

/** Without `createBOL` only the pickup request is booked, so BOL-only details are left off. */
export function buildAwardLoadsPayload(
  model: ShipmentFormModel,
  loadId: string,
  quoteId: string,
  createBOL: boolean,
): ApiAwardLoadsPayload {
  return buildAwardLoadsPayloadCommon({
    createBOL,
    ...(createBOL && model.billTo && { billTo: model.billTo }),
    ...(createBOL &&
      model.customsBrokers && {
        customsBroker: buildLoadCustomsBrokerDTO(model.customsBrokers),
      }),
    ...(createBOL &&
      model.emergencyContact && { emergencyContact: model.emergencyContact }),
    loads: [
      {
        loadId,
        quoteId,
        equipment: {
          accessorials: model.equipment.accessorials || [],
        },
        ...(createBOL && {
          items: model.items.map(item =>
            buildHandlingUnitDTO(model.workflow)({
              ...item,
              dropLocationSequence:
                item.dropLocationSequence ?? model.locations.length - 1,
            }),
          ),
        }),
        ...(model.deliveryOption && { deliveryOption: model.deliveryOption }),
      },
    ],
    locations: model.locations,
    ...(model.rateLocationType && { rateLocationType: model.rateLocationType }),
  })
}
